import React, { createContext, useContext } from 'react';
import { BlogPost, BlogPostInput, BlogCommentInput, BlogFilters } from '../types/blog';
import { featureAvailability } from '../config/featureAvailability';

interface BlogContextType {
  posts: BlogPost[];
  loading: boolean;
  error: string | null;
  disabled: boolean;
  disabledMessage: string;
  createPost: (post: BlogPostInput) => Promise<void>;
  updatePost: (id: string, post: Partial<BlogPostInput>) => Promise<void>;
  deletePost: (id: string) => Promise<void>;
  flagPost: (id: string, reason: string) => Promise<void>;
  addComment: (postId: string, comment: BlogCommentInput) => Promise<void>;
  deleteComment: (postId: string, commentId: string) => Promise<void>;
  flagComment: (postId: string, commentId: string, reason: string) => Promise<void>;
  likePost: (postId: string) => Promise<void>;
  likeComment: (postId: string, commentId: string) => Promise<void>;
  filterPosts: (filters: BlogFilters) => Promise<void>;
  uploadImage: (file: File) => Promise<string>;
  unpublishPost: (id: string) => Promise<void>;
}

const blogFeature = featureAvailability.therapistBlog;
const BLOG_DISABLED_MESSAGE = blogFeature.description;

const BlogContext = createContext<BlogContextType | undefined>(undefined);

const disabledAction = async (): Promise<void> => {
  throw new Error(BLOG_DISABLED_MESSAGE);
};

export const BlogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value: BlogContextType = {
    posts: [],
    loading: false,
    error: null,
    disabled: !blogFeature.available,
    disabledMessage: BLOG_DISABLED_MESSAGE,
    createPost: disabledAction,
    updatePost: disabledAction,
    deletePost: disabledAction,
    flagPost: disabledAction,
    addComment: disabledAction,
    deleteComment: disabledAction,
    flagComment: disabledAction,
    likePost: disabledAction,
    likeComment: disabledAction,
    filterPosts: async () => undefined,
    uploadImage: disabledAction,
    unpublishPost: disabledAction,
  };

  return <BlogContext.Provider value={value}>{children}</BlogContext.Provider>;
};

export const useBlog = () => {
  const context = useContext(BlogContext);
  if (!context) {
    throw new Error('useBlog must be used within a BlogProvider');
  }
  return context;
};
